import type { AxiosInstance } from 'axios'
import type { Alpha3Code } from 'i18n-iso-countries'

import type {
  AgentVerificationState,
  AgentVerificationStatus
} from '@percent/admin-dashboard/api/actions/admin/admin.types'
import type { OrganisationTypes } from '@percent/domain/verifications'
import { AgentVerificationStatus as BEAgentVerificationStatus } from '@percent/domain/verifications'
import type { PBPGetResponse } from '@percent/admin-dashboard/services/pbpResponses'

export type agentVerificationServiceCapabilities = {
  percentClient: AxiosInstance
}

export type activityCheckServiceCapabilities = {
  percentClient: AxiosInstance
}

export type AgentVerificationList = {
  id: string
  firstName: string
  lastName: string
  createdAt: string
  documentUploadedAt: string | null
  email: string
  status: AgentVerificationStatus
  userVerified: boolean
  partner: {
    name: string
  }
  validationRequestId?: string
  organisationId?: string
  title?: string
  rejectionReason?: string
  organisation: {
    name: string
    countryCode: Alpha3Code
  }
  validationRequest?: {
    name: string
    countryCode?: Alpha3Code
  }
  emailVerifiedAt?: string
  reviewedBy?: string
  agentVerificationLogs?: AgentVerificationLog[]
}

export type GetAgentVerificationsProps = {
  page?: number
  pageSize?: number
  cursor?: string | null
  startDate?: string | null
  endDate?: string | null
  sort?: string | null
  direction?: string | null
  status?: AgentVerificationStatus
  state?: AgentVerificationState
}

export type AgentVerification = {
  createdAt: string
  email: string
  emailVerifiedAt: string | null
  firstName: string
  id: string
  lastName: string
  configurationName: string | null
  configurationId: string
  language: string
  organisationId: string
  organisationWebsite: string | null
  partnerId: string
  partnerName: string | null
  rejectionReasonCode: AgentVerificationRejectionReasonDropdownCodes | null
  reviewedBy: string | null
  status: AgentVerificationStatus
  title: string
  updatedAt: string
  validationRequest: string | null
  validationRequestId: string
  validationSubmissionId: string | null
  externalActionRequired: boolean
  fraudulentFlags?: FraudulentFlag[]
  configuration?: {
    allowedOrganisations?: {
      types?: OrganisationTypes[]
    }
    isStatusDynamic?: boolean
  }
}

export enum AgentVerificationRejectionReasonDropdownCodes {
  UserFailedPercentReview = 'user_failed_percent_review',
  Reapply = 'reapply',
  FakeEmailUsed = 'fake_email_used',
  NoOnlinePresence = 'no_online_presence',
  InvalidDocumentation = 'invalid_documentation',
  BlankCorruptedDocumentation = 'blank_corrupted_documentation',
  MismatchedInformation = 'mismatched_information',
  FakeDocumentation = 'fake_documentation',
  SuspiciousApplication = 'suspicious_application',
  InvalidRole = 'invalid_role',
  ExpiredDocumentation = 'expired_documentation',
  Other = 'other'
}

export enum AgentVerificationApprovalReasonDropdownCodes {
  AvDocumentAcceptable = 'av_document_acceptable',
  GoodstackUpdate = 'goodstack_update'
}

export enum AgentVerificationRejectionReasonBackendCodes {
  ValidationRequestFailed = 'validation_request_failed',
  UserVerificationExpired = 'user_verification_expired',
  NonprofitAssociationExpired = 'nonprofit_association_expired',
  DeferToPartner = 'defer_to_partner',
  EligibilitySubscriptionFailed = 'eligibility_subscription_failed',
  KycVerificationFailed = 'kyc_verification_failed',
  KycVerificationExpired = 'kyc_verification_expired'
}

export type AgentVerificationRejectionReasonCodes =
  | AgentVerificationRejectionReasonDropdownCodes
  | AgentVerificationRejectionReasonBackendCodes

export type RejectAgentVerificationArgs = {
  id: string
  rejectionReasonCode: AgentVerificationRejectionReasonDropdownCodes
  fraudulentFlags?: string[]
}

export type ApproveAgentVerificationArgs = {
  id: string
  approvalReasonCode?: AgentVerificationApprovalReasonDropdownCodes
}

export type FraudulentFlagScope = 'organisation' | 'agent'
export type FraudulentFlagCode =
  | 'organisation_website_flagged_third_party_site'
  | 'organisation_website_suspicious_website_content'
  | 'organisation_website_flagged_registrar'
  | 'organisation_website_newly_created_domain'
  | 'organisation_website_suspicious_url'
  | 'organisation_email_newly_created_domain'
  | 'organisation_email_flagged_registrar'
  | 'agent_suspicious_document'
  | 'agent_suspicious_email'
  | 'agent_reused_email'
  | 'agent_newly_created_domain'
  | 'agent_unrelated_language'
  | 'agent_subscription_abuse'
  | 'agent_unsupported_region'

export type FraudulentFlag = {
  id: string
  code: FraudulentFlagCode
  scope: FraudulentFlagScope
}

export type FraudulentFlagsResponse = {
  data: FraudulentFlag[]
}

export type AgentVerificationLogType =
  | 'association_checks_approved'
  | 'association_checks_rejected'
  | 'identity_checks_approved'
  | 'identity_checks_rejected'
  | 'manually_approved'
  | 'manually_rejected'
  | 'association_checks_expired'
  | 'identity_checks_expired'
  | 'organisation_domain_change'
  | 'organisation_email_change'
  | 'domain_verified'
  | 'domain_matched'
  | 'domain_not_matched'
  | 'email_domain_matched'
  | 'email_domain_not_matched'
  | 'email_on_trusted_school_domain'
  | 'agent_verification_created'
  | 'agent_email_common_domain_detected'
  | 'association_email_sent'
  | 'association_email_failed_to_send'
  | 'association_email_accepted'
  | 'association_email_rejected'
  | 'association_email_expired'
  | 'inbox_verification_email_sent'
  | 'inbox_verification_failed_to_send'
  | 'inbox_verification_confirmed'
  | 'domain_verification_automated_pass'
  | 'domain_verification_automation_failed'
  | 'domain_verification_flags_detected'
  | 'domain_verification_start_failed'
  | 'domain_verification_started'
  | 'validation_request_rejected'
  | 'validation_request_accepted'
  | 'found_email_previous_approval'
  | 'found_email_previous_approval_from_different_partner'
  | 'agent_verification_document_requested'
  | 'agent_verification_document_request_email_sent'
  | 'agent_verification_document_request_email_failed_to_send'
  | 'agent_verification_document_uploaded'
  | 'document_reupload_requested'
  | 'basic_document_automated_review_failed'
  | 'av_document_automated_review_pass'
  | 'av_document_ralph_review_dispatched'
  | 'av_document_ralph_review_received'
  | 'validation_submission_failed'
  | 'name_validation_failed'
  | 'name_in_document_not_matched'
  | 'non_acceptable_role_in_document'
  | 'expired_documentation'
  | 'malicious_pdf_producer_detected'
  | 'email_found_on_auto_reject_list'
  | 'domain_found_on_auto_reject_list'
  | 'domain_auto_verified'
  | 'domain_verification_automation_requested'
  | 'defer_to_partner_finished'
  | 'defer_to_partner_skipped'
  | 'blocked_by_policy'
  // Dynamic AV logs
  | 'approve_rejected_dynamic_agent_verification'
  | 'reject_approved_dynamic_agent_verification'
  | 'claim_auto_rejected'
  | 'email_discovery_requested'
  | 'email_enrichment_requested'
  | 'email_enrichment_completed'
  | 'website_enrichment_requested'
  | 'website_enrichment_completed'
  // KYC logs
  | 'kyc_verification_initiated'
  | 'kyc_verification_initiate_failed'
  | 'kyc_verification_email_sent'
  | 'kyc_verification_outcome_email_sent'
  | 'kyc_verification_completed'
  | 'kyc_verification_approved'
  | 'kyc_verification_declined'
  | 'kyc_verification_timed_out'
  | 'kyc_verification_error'
  | 'kyc_verification_not_required'
  // External AV logs
  | 'external_av_not_found'
  | 'external_av_pass'
  | 'external_av_not_pass'
  // OTP logs
  | 'otp_code_verified'
  // Expiry logs
  | 'expiry_set'
  | 'expiry_extended'

export type AgentVerificationLog<T extends Record<string, unknown> = Record<string, unknown>> = {
  id: string
  agentVerificationId: string
  type: AgentVerificationLogType
  createdAt: string
  data?: T
}

export type AgentVerificationLogsResponse = AgentVerificationLog[]

// Shared display shape for the agent-verification "applications" tables (similar applications and
// duplicate-document matches). Both tabs render an identical table of these rows.
export type AgentVerificationApplicationRow = {
  id: string
  email: string
  createdAt: string
  status: BEAgentVerificationStatus
  rejectionReasonCode: AgentVerificationRejectionReasonCodes | null
  organisationId: string | null
  organisationName: string | null
  partnerName: string | null
}

export type AgentVerificationSimilar = AgentVerificationApplicationRow

export type AgentVerificationSimilarResponse = AgentVerificationSimilar[]

export type AgentVerificationMatchType = 'exact' | 'close'

export type AgentVerificationMatchedDocument = AgentVerificationApplicationRow & {
  // Ids of the documents shared with the current verification. Returned by the API but not
  // surfaced as a column; the document-comparison view uses them.
  documentIds: string[]
  matchType: AgentVerificationMatchType
}

export type AgentVerificationMatchedDocumentsResponse = PBPGetResponse<AgentVerificationMatchedDocument[]> & {
  totalCount?: number
}

export type FraudCheckResult = 'pass' | 'fail' | 'needs_review'
type AnalysedDomainSource =
  | 'organisation_website'
  | 'applicant_email'
  | 'organisation_email'
  | 'suggested_website'
  | 'validation_request_website'

type AnalysedDomain = {
  domain: string
  source: AnalysedDomainSource | AnalysedDomainSource[]
}

type BaseFraudCheckData<T> = T & { weight: number }

type RegistrarBucket = 'blocked' | 'monitored' | 'standard'

type EmailScoreCluster = 'very_low' | 'low' | 'review' | 'high' | 'very_high'

export type SuspiciousEmailVerdict = 'SUSPICIOUS' | 'LEGITIMATE' | 'UNDETERMINED'

type EmailRiskSignals = {
  score?: number
  score_cluster?: EmailScoreCluster
  reason_codes?: string
  status?: string
  sub_status?: string
  did_you_mean?: string
  is_disposable?: boolean
  is_spamtrap?: boolean
  is_free?: boolean
  has_stop_words?: boolean
  domain_age?: number
  parsed_name_is_valid?: boolean
  parsed_name_risk_type?: string
  data_breaches_count?: number
  first_seen_days?: number
  identity_ip_count?: number
  identity_msisdn_count?: number
  identity_name_count?: number
}

type IpScoreCluster = 'very_low' | 'low' | 'review' | 'high' | 'very_high'

type IpRiskSignals = {
  score?: number
  score_cluster?: IpScoreCluster
  reason_codes?: string
  is_vpn?: boolean
  is_proxy?: boolean
  proxy_name?: string
  proxy_type?: string
  is_tor?: boolean
  is_relay?: boolean
  is_hosting?: boolean
  connection_type?: string
  connection_speed?: string
  is_mobile?: boolean
  remote_desktop_enable?: boolean
  black_list_count?: number
  xbl_count?: number
  xbl_in_days?: number
  css_count?: number
  css_in_days?: number
  identity_email_count?: number
  identity_msisdn_count?: number
  identity_name_count?: number
  country?: string
  is_valid_format?: boolean
  is_private_internal_address?: boolean
}

export type ApplicationFraudCheck = {
  type: 'application_check'
  data: BaseFraudCheckData<
    | {
        checkName: 'detect_minor_typos'
        emails: {
          agent: string
          organisation: string
          distance: number
        } | null
        websites: {
          agent: string
          organisation: string
          distance: number
        } | null
      }
    | {
        checkName: 'has_applied_for_different_countries'
        countries: string[]
        applicationCountry: string | undefined
      }
    | {
        checkName: 'has_too_many_applications_across_partners'
        count: number
      }
    | {
        checkName: 'language_match'
        analysis: string | null
        applicationLanguage: string | null
      }
    | {
        checkName: 'applications_with_same_organisation_over_threshold'
        email: string
        threshold: number
        organizationId: string
        applicationCount: number
      }
    | {
        checkName: 'applications_with_same_email_over_threshold'
        email: string
        threshold: number
        applicationCount: number
      }
    | {
        checkName: 'has_too_many_applications_same_partner'
        count: number
        email: string
        partnerId: string
        threshold: number
      }
    | {
        checkName: 'has_alias_email'
        email: string
        hasAlias: boolean
      }
    | {
        checkName: 'has_duplicate_applicant_name_same_organisation'
        fullName: string
        firstName: string
        lastName: string
        duplicateCount: number
        maxAllowedDuplicates: number
      }
    | {
        checkName: 'applications_with_same_name_same_configuration_over_threshold'
        duplicateCount: number
        maxAllowedDuplicates: number
      }
    | {
        checkName: 'has_generated_applicant_identity'
        signals: ('generated_name_pair' | 'name_plus_digits_mailbox')[]
        hasGeneratedFirstName: boolean
        hasGeneratedSurname: boolean
        isConsumerMailbox: boolean
        emailDomain: string
      }
    | {
        checkName: 'approved_applications_same_organisation_over_10'
        threshold: number
        organizationId: string
        approvedApplicationCount: number
      }
    | {
        checkName: 'approved_applications_same_organisation_over_50'
        threshold: number
        organizationId: string
        approvedApplicationCount: number
      }
    | {
        checkName: 'registry_trust_score'
        registryId: string
        isLowTrust: boolean
        reason: string | null
      }
    | {
        checkName: 'has_same_first_and_last_name'
        isSameName: boolean
      }
    | {
        checkName: 'has_disposable_email'
        emails: { email: string; isDisposable: boolean }[]
      }
    | {
        checkName: 'has_disallowed_email_substring'
        email: string
        hasDisallowedSubstring: boolean
        matchedSubstrings: string[]
      }
    | {
        checkName: 'email_risk_score'
        email: string
        score: number | null
        scoreCluster: EmailScoreCluster | null
        signals: EmailRiskSignals
      }
    | {
        checkName: 'ip_risk_score'
        score: number | null
        scoreCluster: IpScoreCluster | null
        signals: IpRiskSignals
      }
    | {
        checkName: 'ai_suspicious_email_analysis'
        email: string
        verdict: SuspiciousEmailVerdict
        anomalyScore: number
        detectedIndicators: string[]
        localPartEntropy: number
        firewallTripped: boolean
        schemaValidationFailed: boolean
        promptVersion?: number
      }
    | {
        checkName: 'approved_applications_same_email_same_configuration_over_threshold'
        approvedApplicationCount: number
        threshold: number
      }
    | {
        checkName: 'domain_age_score'
        analysedDomains: { domainAge: number; domainAgeScore: number }[]
      }
    | {
        checkName: 'high_application_velocity_by_configuration'
        configurationId: string
        windowMinutes: number
        count: number
        baselineWindowDays: number
        hourOfDayUtc: number
        expectedCount: number
        standardDeviation: number
        upperControlLimit: number
        zScoreThreshold: number
        minWindowCount: number
      }
    | {
        checkName:
          | 'anomalous_application_volume_by_configuration_1d'
          | 'anomalous_application_volume_by_configuration_7d'
          | 'anomalous_application_volume_by_configuration_30d'
        configurationId: string
        windowDays: number
        baselineWindowDays: number
        baselineMean: number
        baselineStdDev: number
        zScoreThreshold: number
        minWindowCount: number
        total: number
        ratePerDay: number
        zScore: number | null
      }
    | {
        checkName: 'registrar_reputation'
        analysedDomains: (AnalysedDomain & {
          registrar: string | undefined
          matchedRegistrarPattern: string | null
          bucket: RegistrarBucket
          weight: number
        })[]
        highestBucket: RegistrarBucket
      }
    | {
        checkName: 'has_lookalike_email_domain'
        emailDomain: string
        comparedDomains: string[]
        matchedDomain: string
        signal: string
        distance: number
      }
    | {
        checkName: 'ai_misspelt_org_name_email_domain'
        emailDomain: string
        organisationName: string
        closestNameSpelling: string | null
        difference: string | null
        probability: number | null
        model: string | null
      }
    | {
        checkName: 'anomalous_application_volume_by_organisation'
        organisationId: string
        reason: 'spike_above_baseline' | 'burst_with_short_history' | null
        windowDays: number
        baselineWindowDays: number
        baselineMean: number
        baselineStdDev: number
        baselineDaysWithTraffic: number
        minBaselineDays: number
        zScoreThreshold: number
        minWindowCount: number
        total: number
        shortHistoryWindowTotal: number
        ratePerDay: number
        zScore: number | null
      }
    | {
        checkName: 'submissions_same_organisation_same_configuration_over_threshold'
        organisationId: string
        submissionCount: number
        threshold: number
      }
    | {
        checkName: 'succeeded_submissions_same_organisation_same_configuration_over_threshold'
        organisationId: string
        succeededSubmissionCount: number
        threshold: number
      }
    | {
        checkName: 'applicant_ip_country_mismatch'
        applicantCountry: string | null
        organisationCountry: string | null
        isMismatch: boolean | null
        countrySource: 'trustfull_ip'
      }
    | {
        checkName: 'has_icloud_relay_email'
        emailDomain: string
        isRelay: boolean
        relayType?: 'sign_in_with_apple' | 'hide_my_email'
      }
  >
}

export type BasicDomainFraudCheck = {
  type: 'basic_domain_check'
  data: BaseFraudCheckData<
    | {
        checkName: 'disallowed_domain_list'
        analysedDomains: (AnalysedDomain & { isDisallowed: boolean })[]
      }
    | {
        checkName: 'has_website_hosting_provider_subdomain'
        analysedDomains: (AnalysedDomain & { hasWebsiteHostingProviderSubdomain: boolean })[]
      }
    | {
        checkName: 'has_free_hosting_cname'
        analysedDomains: (AnalysedDomain & {
          hasFreeHostingCname: boolean
          detectedProvider: string | null
          cnameTarget: string | null
        })[]
      }
    | {
        checkName: 'can_website_be_opened'
        analysedDomains: (AnalysedDomain & { canBeOpened: boolean })[]
      }
    | {
        checkName: 'is_website_empty'
        analysedDomains: (AnalysedDomain & { isWebsiteEmpty: boolean })[]
      }
    | {
        checkName: 'has_website_placeholder_content'
        analysedDomains: (AnalysedDomain & { hasWebsitePlaceholderContent: boolean })[]
      }
    | {
        checkName: 'has_website_redirect'
        analysedDomains: (AnalysedDomain & { hasWebsiteRedirect: boolean })[]
      }
    | {
        checkName: 'is_ssl_certificate_valid'
        analysedDomains: (AnalysedDomain & { isSslCertificateValid: boolean })[]
      }
    | {
        checkName: 'has_high_risk_tld'
        analysedDomains: (AnalysedDomain & { isHighRisk: boolean; reason: string | null })[]
      }
    | {
        checkName: 'website_legitimacy_analysis'
        analysedDomains: (AnalysedDomain & {
          isWebsiteLegitimate: boolean
          signals: {
            hasTeamPage: boolean
            hasAboutPage: boolean
            hasContactPage: boolean
            hasPhoneNumber: boolean
            contentWordCount: number
            internalPageCount: number
            hasPhysicalAddress: boolean
            hasSocialMediaLinks: boolean
            contactEmailMatchesDomain: boolean
          }
          aiAnalysis: {
            isGenericOrTemplated: boolean
            isAiGenerated: boolean
            hasSpecificActivities: boolean
            legitimacyScore: number
            reasoning: string
          } | null
          pagesAnalysed: string[]
          aiAnalysisFailed: boolean
          extractionFailed: boolean
          websiteUnreachable: boolean
        })[]
      }
    | {
        checkName: 'has_country_tld_mismatch'
        analysedDomains: (AnalysedDomain & {
          isMismatch: boolean
          tld: string | null
          tldCountryCode: string | null
          organisationCountryCode: string | null
        })[]
      }
    | {
        checkName: 'has_email_domain_redirect_to_organisation'
        analysedDomains: (AnalysedDomain & {
          hasEmailDomainRedirectToOrganisation: boolean
          finalDomain: string
          nearCopyOf: string
          distance: number
          comparedDomains: string[]
        })[]
      }
    // Results stored before the API returned analysedDomains for this check. They can't be grouped by domain.
    | {
        checkName: 'has_email_domain_redirect_to_organisation'
        distance: number
        nearCopyOf: string
        emailDomain: string
        finalDomain: string
        comparedDomains: string[]
      }
  >
}

type GeolocationFlag = 'suspicious_asn' | 'geolocation_lookup_failed'
type WhoIsFlag = 'recently_registered_domain' | 'suspicious_registrar' | 'whois_lookup_failed'

export type AdvancedDomainFraudCheck = {
  type: 'advanced_domain_check'
  data: BaseFraudCheckData<
    | {
        checkName: 'geolocation_check'
        analysedDomains: (AnalysedDomain & {
          flags?: GeolocationFlag[]
          asn?: number
          ip?: string
          isp?: string
          location?: {
            city?: string
            country?: string
            region?: string
            lat?: number
            lng?: number
          }
        })[]
      }
    | {
        checkName: 'who_is_check'
        analysedDomains: (AnalysedDomain & {
          flags?: WhoIsFlag[]
          registrar?: string
          registeredDate?: string
          domainAge?: number
        })[]
      }
  >
}

export type DocumentFraudCheck = {
  type: 'document_check'
  data: BaseFraudCheckData<
    | {
        checkName: 'document_fraud_analysis'
        validationSubmissionDocumentId: string
        recommendation: string
      }
    | {
        checkName: 'document_ai_generated_detection'
        validationSubmissionDocumentId: string
        overallAIScore: number
      }
    | {
        checkName: 'document_malicious_pdf_producer'
        validationSubmissionDocumentId: string
        producer: string | null
      }
    | {
        checkName: 'document_modified_pdf_metadata'
        validationSubmissionDocumentId: string
        producer: string | null
        isModified: boolean
      }
    | {
        checkName: 'duplicate_document'
        validationSubmissionDocumentId: string
        matchingDocumentIds: string[]
      }
    | {
        checkName: 'similar_document'
        validationSubmissionDocumentId: string
        matchingDocumentIds: string[]
      }
    | {
        checkName: 'document_suspicious_pdf_producer'
        validationSubmissionDocumentId: string
        producer: string | null
      }
    | {
        checkName: 'creation_and_modification_date_mismatch'
        validationSubmissionDocumentId: string
        creationDate: string
        modificationDate: string
      }
    | {
        checkName: 'metadata_stripped'
        validationSubmissionDocumentId: string
      }
    | {
        checkName: 'similar_to_fraud_document'
        validationSubmissionDocumentId: string
        similarDocumentsCapped: boolean
        matchedAgentVerificationCount: number
        approvedAgentVerificationCount: number
        markedAsFraudDocumentCount: number
        fraudRejectedAgentVerificationCount: number
        fraudEvidenceMatches: {
          agentVerificationId: string
          documentId: string
          documentFraudCode: string | null
          rejectionReasonCode: string | null
        }[]
      }
  >
}

export type FraudCheck = {
  id: string
  result: 'pass' | 'fail'
  validationSubmissionId: string
} & (ApplicationFraudCheck | BasicDomainFraudCheck | AdvancedDomainFraudCheck | DocumentFraudCheck)
export type FraudCheckType = FraudCheck['type']

export type FraudCheckResultsResponse = {
  result: FraudCheckResult | null
  checkResults: FraudCheck[]
  isMarkedAsSafe: boolean
}

// Only the agent verification endpoint scores the application; the validation request one has no fraudScore.
export type AgentVerificationFraudCheckResultsResponse = FraudCheckResultsResponse & {
  fraudScore: number | null
}

type GeoLocationRecord = Partial<{
  ip: string
  isp: string
  location: {
    country: string
    region: string
    city: string
  }
  as: {
    asn: string
  }
}>

type WhoisRecord = Partial<{
  registrarName: string
  createdDate: string
  estimatedDomainAge: number
}>

export type DomainVerificationFlag =
  | 'whois_lookup_failed'
  | 'recently_registered_domain'
  | 'suspicious_registrar'
  | 'geolocation_lookup_failed'
  | 'suspicious_asn'
  | 'unknown_website_accessibility'
  | 'inaccessible_website'
  | 'ssl_certificate_issues'
  | 'suspicious_redirects'
  | 'empty_website'
  | 'placeholder_content'
  | 'hosting_provider_subdomain'

export type GlobalFlag =
  | 'multiple_applications_with_same_email'
  | 'language_does_not_match_country'
  | 'suspicious_values_detected_from_openai'
  | 'no_organisation_website'
  | 'applicant_email_common_domain'
  | 'organisation_email_common_domain'
  | 'domain_analysis_failed'

export type DomainSource =
  | 'organisation_website'
  | 'applicant_email'
  | 'organisation_email'
  | 'suggested_website'
  | 'validation_request_website'

export type DomainVerification = {
  source: DomainSource
  domain?: string
  whoisData?: WhoisRecord | null
  geoData?: GeoLocationRecord | null
  flags?: DomainVerificationFlag[]
}

export type LanguageMatchResponse = {
  match: boolean
  analysis: string
}

export type FraudIndicatorsResponse = {
  suspicious: boolean
  suspiciousIndicators: string[]
  analysis: string
}

export type OpenAiAnalysis = {
  languageMatch: LanguageMatchResponse
  fraudIndicators?: FraudIndicatorsResponse
}

export type DomainVerificationResult = {
  globalFlags: GlobalFlag[]
  domains: DomainVerification[]
  openAiAnalysis: OpenAiAnalysis
}

export type DomainVerificationResponse = Partial<{
  id: string
  agentVerificationId: string
  result: DomainVerificationResult
}>

export type KycVerificationStatus =
  | 'pending'
  | 'approved'
  | 'declined'
  | 'review'
  | 'awaiting_input'
  | 'abandoned'
  | 'error'
  | 'timed_out'

export type KycRiskLevel = 'low' | 'moderate' | 'high' | 'very_high'

export type KycVerificationProvider = 'entrust'

export type KycVerificationRecord = {
  id: string
  provider: KycVerificationProvider
  status: KycVerificationStatus
  createdAt: string
  riskLevel: KycRiskLevel | null
  fraudScore: number | null
  workflowReference: string | null
  providerWorkflowRunId: string | null
  dashboardLinkUrl: string | null
  updatedAt: string | null
  completedAt: string | null
  expiresAt: string | null
}

export type KycVerificationResponse = KycVerificationRecord | Record<string, never>

export const BulkRejectRowErrorCode = {
  InvalidData: 'invalidData',
  DuplicateAgentVerificationId: 'duplicateAgentVerificationId'
} as const

export type BulkRejectRowErrorCode = typeof BulkRejectRowErrorCode[keyof typeof BulkRejectRowErrorCode]

export type BulkRejectRowError = {
  row: number
  field: string
  code: BulkRejectRowErrorCode
  message: string
  fieldValue?: string
}

export type BulkRejectAgentVerificationsResult = {
  batchId: string
  total: number
}
